type Report = {
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  context?: string;
  serverErrorId?: string;
};

let tokenProvider: (() => string | null) | null = null;

export function setErrorReporterToken(getter: () => string | null): void {
  tokenProvider = getter;
}

export async function reportError(report: Report): Promise<string | null> {
  const token = tokenProvider?.();
  if (!token) return null;
  try {
    const res = await fetch("/api/error-report", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-graph-token": token
      },
      body: JSON.stringify({
        ...report,
        url: report.url ?? window.location.href,
        userAgent: report.userAgent ?? navigator.userAgent
      })
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { errorId?: string };
    return body.errorId ?? null;
  } catch {
    return null;
  }
}

export function installGlobalErrorHandlers(): void {
  window.addEventListener("error", (ev) => {
    void reportError({
      message: ev.message || "window.onerror",
      stack: ev.error instanceof Error ? ev.error.stack : undefined,
      context: `${ev.filename}:${ev.lineno}:${ev.colno}`
    });
  });

  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev.reason;
    void reportError({
      message: reason instanceof Error ? reason.message : String(reason),
      stack: reason instanceof Error ? reason.stack : undefined,
      context: "unhandledrejection"
    });
  });
}

/**
 * Wrap a fetch call so that non-2xx responses are reported automatically,
 * and the server's x-error-id (if present) is surfaced in the thrown Error.
 */
export async function fetchWithReporting(
  input: RequestInfo | URL,
  init?: RequestInit,
  context?: string
): Promise<Response> {
  const res = await fetch(input, init);
  if (!res.ok) {
    const serverErrorId = res.headers.get("x-error-id") ?? undefined;
    let bodyText = "";
    try {
      bodyText = await res.clone().text();
    } catch { /* ignore */ }
    void reportError({
      message: `HTTP ${res.status} ${res.statusText} on ${typeof input === "string" ? input : input.toString()}`,
      context: context ?? "api",
      stack: bodyText.slice(0, 1000),
      serverErrorId
    });
    const err = new Error(
      `HTTP ${res.status}${serverErrorId ? ` [errorId=${serverErrorId}]` : ""}: ${bodyText.slice(0, 600)}`
    ) as Error & { serverErrorId?: string; status?: number };
    err.serverErrorId = serverErrorId;
    err.status = res.status;
    throw err;
  }
  return res;
}
